import {
  Check,
  ChevronsUpDown,
  LogOut,
  Monitor,
  Moon,
  Sun,
} from "lucide-react";
import { type Theme, useTheme } from "../../contexts/themeContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { cn } from "../../lib/utils";

const OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

interface ThemeToggleProps {
  isCollapsed: boolean;
  email: string | undefined;
  onLogout: () => void;
  onOpenChange: (open: boolean) => void;
}

/** Account menu opened from the signed-in email: theme picker and logout. */
export const ThemeToggle = ({
  isCollapsed,
  email,
  onLogout,
  onOpenChange,
}: ThemeToggleProps) => {
  const { available, theme, setTheme } = useTheme();

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-2 w-full p-3 hover:bg-stone-50 border border-transparent text-sm cursor-pointer transition-colors duration-200 rounded-xs",
            isCollapsed && "hidden",
          )}
          // Leads with the visible email so speech input can target it.
          aria-label={
            email ? `${email}, account and theme` : "Account and theme"
          }
          data-testid="app-theme-toggle"
        >
          <span
            data-testid="app-signed-in-email"
            className="flex-1 text-left truncate"
          >
            {email}
          </span>
          <ChevronsUpDown size={14} className="text-stone-400 flex-shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className="min-w-[220px]">
        <DropdownMenuLabel className="text-xs text-stone-500 font-normal truncate">
          {email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {available ? (
          <>
            <DropdownMenuLabel className="text-xs text-stone-500 font-normal">
              Theme
            </DropdownMenuLabel>
            {OPTIONS.map(({ value, label, icon: Icon }) => (
              <DropdownMenuItem
                key={value}
                role="menuitemradio"
                aria-checked={theme === value}
                onSelect={() => setTheme(value)}
                className="flex items-center gap-2"
              >
                <Icon size={14} className="flex-shrink-0" />
                <span className="flex-1">{label}</span>
                {theme === value ? (
                  <Check size={14} className="text-stone-500 flex-shrink-0" />
                ) : null}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuItem
          onSelect={onLogout}
          className="flex items-center gap-2"
        >
          <LogOut size={14} className="flex-shrink-0" />
          <span className="flex-1">Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
